// src/services/adminDashboardService.js
import { repo } from '../dbservice/repo';

export const getCompanyData = () => {
  try {
    const raw = localStorage.getItem('companyData');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.id) {
      console.warn('[adminDashboardService] companyData missing id field');
      return null;
    }
    return {
      id:                   parsed.id,
      name:                 parsed.name || 'Unknown Company',
      companyId:            parsed.id,
      localSupabaseURL:     parsed.localSupabaseURL     || '',
      localSupabaseAnonKey: parsed.localSupabaseAnonKey || '',
    };
  } catch (err) {
    console.error('[adminDashboardService] getCompanyData failed:', err);
    return null;
  }
};

export const initializeDataLayer = async (onProgress, onError) => {
  repo.onProgressUpdate      = onProgress || null;
  repo.onInitializationError = onError    || null;
  try {
    return await repo.probeLocalServer();
  } catch (err) {
    console.warn('[adminDashboardService] probeLocalServer threw:', err?.message);
    return { success: false };
  } finally {
    // Always clean up - even if probe throws
    repo.onProgressUpdate      = null;
    repo.onInitializationError = null;
  }
};


export const subscribeToAssets = (
  companyId,
  role,
  locationCode,
  onData,
  onError,
) => {
  if (!companyId) {
    console.error('[adminDashboardService] subscribeToAssets: companyId required');
    return () => {};
  }

  const filters = [{ field: 'companyId', value: companyId }];
  if (role === 'locationAdmin' && locationCode) {
    filters.push({ field: 'locationCode', value: locationCode });
  }

  let result;
  try {
    result = repo.subscribeToUpdates(
      'assets',
      filters,
      [],
      null,
      (assets) => {
        try {
          onData(Array.isArray(assets) ? assets : []);
        } catch (handlerErr) {
          console.error('[adminDashboardService] assets handler threw:', handlerErr);
          onError?.(handlerErr);
        }
      },
    );
  } catch (err) {
    console.error('[adminDashboardService] subscribeToAssets setup failed:', err);
    return () => {};
  }


  if (result instanceof Promise) {
    console.warn('[adminDashboardService] Got Promise from subscribeToUpdates, wrapping...');
    let resolvedUnsub = null;
    result
      .then((fn) => {
        if (typeof fn === 'function') resolvedUnsub = fn;
      })
      .catch((err) => {
        console.error('[adminDashboardService] subscribeToUpdates Promise rejected:', err);
      });
    // Return a sync wrapper that calls resolved fn when available
    return () => {
      if (typeof resolvedUnsub === 'function') resolvedUnsub();
    };
  }

  
  if (typeof result === 'function') return result;

  
  console.error('[adminDashboardService] subscribeToAssets got unexpected return type:', typeof result);
  return () => {};
};

/**
 * Always returns a callable function, never a Promise
 */
export const subscribeToUsers = (companyId, onData, onError) => {
  if (!companyId) {
    console.error('[adminDashboardService] subscribeToUsers: companyId required');
    return () => {};
  }

  const filters = [{ field: 'companyId', value: companyId }];

  let result;
  try {
    result = repo.subscribeToUpdates(
      'users',
      filters,
      [],
      null,
      (users) => {
        try {
          onData(Array.isArray(users) ? users : []);
        } catch (handlerErr) {
          console.error('[adminDashboardService] users handler threw:', handlerErr);
          onError?.(handlerErr);
        }
      },
    );
  } catch (err) {
    console.error('[adminDashboardService] subscribeToUsers setup failed:', err);
    return () => {};
  }

  if (result instanceof Promise) {
    let resolvedUnsub = null;
    result
      .then((fn) => {
        if (typeof fn === 'function') resolvedUnsub = fn;
      })
      .catch((err) => {
        console.error('[adminDashboardService] users subscribeToUpdates rejected:', err);
      });
    return () => {
      if (typeof resolvedUnsub === 'function') resolvedUnsub();
    };
  }

  if (typeof result === 'function') return result;

  console.error('[adminDashboardService] subscribeToUsers got unexpected return type:', typeof result);
  return () => {};
};
/**
 * Load categories / subcategories / brands / locations for a company.
 * Returns plain arrays (never throws — falls back to [] per collection).
 */
export const loadDashboardReferenceData = async (companyId) => {
  if (!companyId) {
    console.warn('[adminDashboardService] loadDashboardReferenceData: companyId required');
    return { categories: [], subcategories: [], brands: [], locations: [] };
  }

  const filter = [{ field: 'companyId', value: String(companyId) }];

  const [categories, subcategories, brands, locations] = await Promise.all([
    repo.fetchData('categories',    filter).catch((e) => {
      console.error('[adminDashboardService] categories fetch failed:', e);
      return [];
    }),
    repo.fetchData('subcategories', filter).catch((e) => {
      console.error('[adminDashboardService] subcategories fetch failed:', e);
      return [];
    }),
    repo.fetchData('brands',        filter).catch((e) => {
      console.error('[adminDashboardService] brands fetch failed:', e);
      return [];
    }),
    repo.fetchData('locations',     filter).catch((e) => {
      console.error('[adminDashboardService] locations fetch failed:', e);
      return [];
    }),
  ]);

  return {
    categories:    Array.isArray(categories)    ? categories    : [],
    subcategories: Array.isArray(subcategories) ? subcategories : [],
    brands:        Array.isArray(brands)        ? brands        : [],
    locations:     Array.isArray(locations)     ? locations     : [],
  };
};